import React, { useState } from 'react';
import { TrajectoryYearSnapshot } from '../utils/financialEngine';
import { Table, Eye } from 'lucide-react';

interface ComparisonTableProps {
  trajectory: TrajectoryYearSnapshot[];
}

const formatCurrency = (val: number) => {
  const rounded = Math.round(val);
  const prefix = rounded < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(rounded).toLocaleString()}`;
};

export const ComparisonTable: React.FC<ComparisonTableProps> = ({ trajectory }) => {
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800/60 pb-3">
        <div>
          <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
            <Table className="w-4 h-4 text-cyan-400" />
            10-Year Annual Net Worth Schedule
          </h3>
          <p className="text-xs text-slate-400">Year-by-year component breakdown comparing Keep & Rent vs Sell & Buy</p>
        </div>
        <button
          onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-300 hover:text-white bg-slate-950/80 hover:bg-slate-800 border border-slate-800 transition-colors w-fit"
        >
          <Eye className="w-3.5 h-3.5 text-cyan-400" />
          <span>{showDetailedBreakdown ? 'Hide Component Detail' : 'Show Equity & Debt Detail'}</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs font-mono">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 font-sans uppercase tracking-wider text-[11px] bg-slate-950/40">
              <th className="py-2.5 px-3">Year</th>
              <th className="py-2.5 px-3 text-right">Sell & Buy NW</th>
              <th className="py-2.5 px-3 text-right">Keep & Rent NW</th>
              <th className="py-2.5 px-3 text-right">Net Worth Delta</th>
              <th className="py-2.5 px-3 text-right">Home #1 Net Rent</th>
              {showDetailedBreakdown && (
                <>
                  <th className="py-2.5 px-3 text-right text-slate-500">Home #1 Equity</th>
                  <th className="py-2.5 px-3 text-right text-slate-500">Home #2 Equity (Keep)</th>
                  <th className="py-2.5 px-3 text-right text-slate-500">HELOC Debt</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {trajectory.map((row) => {
              const diff = row.netWorthDifference;
              const keepWins = diff >= 0;
              return (
                <tr key={row.year} className="hover:bg-slate-800/30 transition-colors">
                  <td className="py-2.5 px-3 font-semibold text-slate-300 font-sans">Year {row.year}</td>
                  <td className="py-2.5 px-3 text-right font-medium text-emerald-400">
                    {formatCurrency(row.sellBuy.netWorth)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-medium text-cyan-400">
                    {formatCurrency(row.keepRent.netWorth)}
                  </td>
                  <td className={`py-2.5 px-3 text-right font-bold ${keepWins ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {keepWins ? '+' : ''}{formatCurrency(diff)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-slate-300">
                    {formatCurrency(row.keepRent.home1MonthlyNetCashFlow)}/mo
                  </td>
                  {showDetailedBreakdown && (
                    <>
                      <td className="py-2.5 px-3 text-right text-slate-400">
                        {formatCurrency(row.keepRent.home1Equity)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-400">
                        {formatCurrency(row.keepRent.home2Equity)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-amber-400/90">
                        {formatCurrency(row.keepRent.helocBalance)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
